import type { FilterableCarItem } from '@/scopes/checkout/filterableCars/domain/types'
import { useIntl } from '@/intl'
import {
  CarCard,
  CarCardBody,
  Price,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/CarOption'
import {
  carIsBeingUpgraded,
  carToBeUpgraded,
  editingCar,
  useCarRentalServiceStore,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/store'
import { addCarToOptionAndUpgradeCar } from '@/scopes/checkout/domain/actions'
import { createCarEvent } from '@/scopes/checkout/domain/analytics'
import { useService } from '@/scopes/checkout/domain/hooks'
import { ServiceType } from '@/scopes/checkout/domain/types'
import { useGetAllCarsQuery, useSelectCar } from '@/scopes/checkout/filterableCars/domain/hooks'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { Button, ListItem, space, tk, tok } from '@sunwebgroup/ui-components'
import * as React from 'react'
import { styled } from 'styled-components'

const FilterableCarContent = styled.div`
  display: grid;
  grid-template-areas:
    "image head"
    "info info"
    "footer footer";
  grid-template-rows: repeat(3, auto);
  grid-template-columns: 120px auto;
  width: 100%;
  gap: ${space('primitives_space_12')};

  @media ${tk('mq_desktop_lg')} {
    padding: ${space('primitives_space_12')};
    grid-template-areas:
      "image head footer"
      "image info footer";
    grid-template-rows: repeat(2, auto);
    grid-template-columns: 160px auto 120px;
  }
`

const FilterableCarContentBody = styled(CarCardBody)`
`
const CarCardFooter = styled.div`
  background-color: ${tok('semantics_views_tinted_background_color')};
  width: 100%;
  grid-area: footer;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: ${space('primitives_space_12')};
  padding: ${space('primitives_space_12')};

  @media ${tk('mq_desktop_lg')} {
    background-color: transparent;
    flex-direction: column;
    justify-content: center;
    padding: ${space('primitives_space_0')};
  }
`
const ActionButtons = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;

  gap: ${space('primitives_space_12')};

  @media ${tk('mq_desktop_lg')} {
    flex-direction: column;
    justify-content: center;
    width: 100%;
    ${Button} {
      width: 100%;
    }
  }
`

export function CarView({ ...car }: FilterableCarItem) {
  return (
    <CarCard key={car.id} $selected={false} as="div">
      <ListItem.Content>
        <CarCardView {...car} />
      </ListItem.Content>
    </CarCard>
  )
}

export function CarCardView({ ...option }: FilterableCarItem) {
  const { value, id, price } = option
  const { sendEvent } = useAnalytics()
  const { service, setServiceValue } = useService(ServiceType.CarRentalFiltering)
  const { selectCar } = useSelectCar()
  const { startEditingCar, setCarToBeSelected, setShowFilterableCars, setShowExtraCars, setCarIsBeingUpgraded, setCarToBeUpgraded, endEditingCar } = useCarRentalServiceStore()
  const isCarBeingUpgraded = useCarRentalServiceStore(carIsBeingUpgraded)
  const carValueToBeUpgraded = useCarRentalServiceStore(carToBeUpgraded)
  const { data: allCarsData } = useGetAllCarsQuery()

  const currentEditingCar = useCarRentalServiceStore(editingCar(carValueToBeUpgraded))
  const intl = useIntl()

  const handleSelectClick = React.useCallback(async () => {
    if (!id)
      return
    if (!service)
      return
    const cars = allCarsData?.cars ?? {}

    selectCar(id)
    if (isCarBeingUpgraded) {
      if (currentEditingCar) {
        setServiceValue(addCarToOptionAndUpgradeCar({ value, participantId: currentEditingCar.driverId ?? '', id })(service, cars))
      }
      setCarIsBeingUpgraded(false)
      setCarToBeUpgraded('')
      endEditingCar(id)
      sendEvent(
        createCarEvent({
          result: 'upgrade',
          type: value,
          description: 'upgrade button clicked',
          element: 'button',
        }),
      )
    }
    else {
      setCarToBeSelected(id)
      setShowExtraCars(false)
      setShowFilterableCars(false)
      startEditingCar(id, {
        driverId: null,
        driverBirthDate: new Date(),
        driverHasCreditCard: false,
        bookedEquipmentsWithQuantity: [],
        isEditing: false,
      })
      sendEvent(
        createCarEvent({
          result: 'Car selected',
          type: value,
          description: 'Filtered car selected',
          element: 'button',
          validated: true,
        }),
      )
    }
  }, [allCarsData, currentEditingCar, endEditingCar, isCarBeingUpgraded, service, setCarIsBeingUpgraded, setCarToBeUpgraded, setServiceValue, selectCar, setShowFilterableCars, sendEvent, setCarToBeSelected, setShowExtraCars, startEditingCar, value, id])

  if (!service)
    return
  return (
    <FilterableCarContent>
      <FilterableCarContentBody {...option} />
      <CarCardFooter>
        <Price as="span" $variant="body-lg-bold">
          {intl.formatTruncatedCurrency(price)}
        </Price>
        <ActionButtons>
          <Button
            $hierarchy="secondary"
            $withoutColor
            onClick={handleSelectClick}
            $size="small"
          >
            {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_select_car_text' })}
          </Button>
        </ActionButtons>
      </CarCardFooter>
    </FilterableCarContent>
  )
}
